import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { EIntegrationTypes, EPackageTiers } from '@feathr/blackbox';
import { Nav, NavMenu } from '@feathr/components';
import { TrackBlocked } from '@feathr/extender/components';
import {
  useGoogleAdsPermissions,
  useLicense,
  useMetaIntegrationStatus,
  useMinPackageTooltip,
} from '@feathr/extender/hooks';
import { useAccount, useFlags, useLocalUrl, useRole } from '@feathr/extender/state';

import { createNavItems } from '../createNav';

function SettingsNav(): JSX.Element {
  const localUrl = useLocalUrl();
  const account = useAccount();
  const { t } = useTranslation();
  const {
    hasBilling,
    hasConversions,
    hasDomains,
    hasIntegrations,
    isSudoer,
    isAdmin,
    hasPermissionsLicenseOrFlag,
  } = useRole();
  const {
    hasImis: hasImisFlag,
    hasBlackbaudRaisersEdge: hasBlackbaudRaisersEdgeFlag,
    hasSavedGeoFilterSets,
  } = useFlags();
  const {
    proResourcesPageVisible,
    iMISDirectIntegration,
    blackbaudDirectIntegration,
    hasApiKeys,
    googleAdsSmart: hasGoogleAdsSmart,
    directIntegrations,
    zapier,
    facebook: hasFacebook,
  } = useLicense([
    'proResourcesPageVisible',
    'iMISDirectIntegration',
    'blackbaudDirectIntegration',
    'hasApiKeys',
    'googleAdsSmart',
    'directIntegrations',
    'zapier',
    'facebook',
  ]);

  const showImis = iMISDirectIntegration || hasImisFlag;
  const showBlackbaudRaisersEdge = blackbaudDirectIntegration || hasBlackbaudRaisersEdgeFlag;
  const googleAdsPermissions = useGoogleAdsPermissions({ requireConnectedCustomer: false });
  const showGoogleAds = googleAdsPermissions || hasGoogleAdsSmart;
  const metaIntegrationStatus = useMetaIntegrationStatus();

  const directIntegrationsTooltip = useMinPackageTooltip('directIntegrations', 'data');
  const zapierTooltip = useMinPackageTooltip('zapier', 'data');
  const integrationsMenuEnabled =
    showImis || showBlackbaudRaisersEdge || showGoogleAds || directIntegrations > 0 || zapier > 0;

  const tooltipsByFeature = {
    directIntegrations: directIntegrationsTooltip,
    zapier: zapierTooltip,
  };

  let integrationsTooltip: string | undefined;

  if (integrationsMenuEnabled) {
    // When integrations menu is enabled, prefer directIntegrations tooltip
    if (tooltipsByFeature.directIntegrations) {
      integrationsTooltip = tooltipsByFeature.directIntegrations;
    } else if (tooltipsByFeature.zapier) {
      integrationsTooltip = tooltipsByFeature.zapier;
    }
  } else {
    // When integrations menu is disabled, prefer zapier tooltip
    if (tooltipsByFeature.zapier) {
      integrationsTooltip = tooltipsByFeature.zapier;
    } else if (tooltipsByFeature.directIntegrations) {
      integrationsTooltip = tooltipsByFeature.directIntegrations;
    }
  }

  const isImisConflicting = account.conflictingIntegrations.includes(EIntegrationTypes.Imis);
  const isBlackbaudConflicting = account.conflictingIntegrations.includes(
    EIntegrationTypes.BlackbaudRaisersEdge,
  );

  const billingNav = (
    <Nav indented={true} label={'Billing settings navigation'} type={'vertical'}>
      {createNavItems(
        [
          [
            '/settings/billing/configurations',
            'Manage billing configurations',
            'settings,billing,configurations',
            'Billing Configurations',
            t('Configurations'),
          ],
          [
            '/settings/billing/license',
            'Manage license settings',
            'settings,billing,license',
            'License',
          ],
          [
            '/settings/billing/invoices',
            'View billing invoices',
            'settings,billing,invoices',
            'Invoices',
          ],
        ],
        { localUrl, t },
      )}
    </Nav>
  );

  const userNav = (
    <Nav
      indented={true}
      label={'User settings navigation'}
      name={'nav_user_menu'}
      type={'vertical'}
    >
      {createNavItems(
        [
          [
            '/settings/user/notifications',
            'Manage notification preferences',
            'settings,notifications,preferences',
            'Notification Settings',
            t('Notifications'),
          ],
          [
            '/settings/user/profile',
            'Manage your user profile',
            'settings,user,profile',
            'User Profile',
            t('Profile'),
          ],
          [
            '/settings/user/reset-password',
            'Change your password',
            'settings,password,reset',
            'Reset Password',
          ],
          [
            '/settings/user/internal',
            'Internal user settings',
            'settings,user,internal,admin',
            'Internal Settings',
            t('Internal'),
          ],
        ],
        {
          localUrl,
          t,
          conditions: { 3: account.isInternal || isSudoer },
        },
      )}
    </Nav>
  );

  const accountNav = (
    <Nav
      indented={true}
      label={'Account settings navigation'}
      name={'nav_account_menu'}
      type={'vertical'}
    >
      {createNavItems(
        [
          [
            '/settings/account/advertisers',
            'Manage advertisers',
            'settings,advertisers,manage',
            'Advertisers',
          ],
          [
            '/settings/account/conversions',
            'Manage conversion tracking',
            'settings,conversions,tracking',
            'Conversions',
          ],
          [
            '/settings/account/domains',
            'Manage account domains',
            'settings,domains,manage',
            'Domains',
          ],
          [
            '/settings/account/allow-list',
            'Manage domain allow list',
            'settings,domains,allow,list,whitelist',
            'Domain Allow List',
          ],
          [
            '/settings/account/emails',
            'Manage email templates',
            'settings,email,templates',
            'Email Templates',
            t('Email Addresses'),
          ],
          [
            '/settings/account/geofilters',
            'Manage geofilters',
            'settings,account,geofilters',
            t('Geofilters'),
          ],
          [
            '/settings/account/templates',
            'Manage account templates',
            'settings,templates,designs',
            'Templates',
          ],
          [
            '/settings/account/ip-filtering',
            'Configure IP filtering settings',
            'settings,ip,filtering,security',
            'IP Filtering',
          ],
          [
            '/settings/account/flags',
            'Manage feature flags',
            'settings,flags,features,admin',
            'Feature Flags',
            t('Flags'),
          ],
          ['/settings/account/fonts', 'Manage account fonts', 'settings,fonts,typography', 'Fonts'],
          [
            '/settings/account/general',
            'General account settings',
            'settings,account,general',
            'General Settings',
            t('General'),
          ],
          [
            '/settings/account/profile',
            'Manage account profile',
            'settings,account,profile',
            'Account Profile',
            t('Profile'),
          ],
          [
            '/settings/account/subscriptions',
            'Manage subscriptions',
            'settings,subscriptions,notifications',
            'Subscriptions',
          ],
          [
            '/settings/account/users',
            'Manage users and permissions',
            'settings,users,roles,permissions',
            hasPermissionsLicenseOrFlag ? t('Users & Roles') : t('Users'),
            hasPermissionsLicenseOrFlag ? t('Users & Roles') : t('Users'),
          ],
        ],
        {
          localUrl,
          t,
          conditions: {
            // The index of these conditions corresponds to the order of the nav items above. Adding an item  will change these indices.
            0: account.get('is_agency') || isSudoer,
            1: hasConversions,
            2: hasDomains,
            3: hasDomains,
            5: !!hasSavedGeoFilterSets,
            8: account.isInternal || isSudoer,
            10: isAdmin || isSudoer,
            11: isSudoer,
            13: isAdmin || isSudoer,
          },
        },
      )}
    </Nav>
  );

  const integrationsNav = (
    <Nav
      indented={true}
      label={'Integrations settings navigation'}
      name={'nav_integrations_menu'}
      type={'vertical'}
    >
      {createNavItems(
        [
          [
            'settings/integrations/api-keys',
            'Manage API keys',
            'settings,integrations,api,keys',
            'API Keys',
          ],
          [
            '/settings/integrations/google-ads',
            'Google Ads integration settings',
            'settings,integrations,google,ads',
            'Google Ads Integration',
            t('Google Ads'),
          ],
          [
            '/settings/integrations/imis',
            'iMIS integration settings',
            'settings,integrations,imis',
            'iMIS Integration',
            t('iMIS'),
          ],
          [
            '/settings/integrations/meta',
            'Meta/Facebook integration settings',
            'settings,integrations,meta,facebook',
            'Meta Integration',
            t('Meta'),
          ],
          [
            '/settings/integrations/raisers-edge',
            "Raiser's Edge NXT integration",
            'settings,integrations,raisers,edge',
            "Raiser's Edge NXT",
            t("Raiser's Edge NXT"),
          ],
        ],
        {
          localUrl,
          t,
          conditions: {
            0: hasApiKeys,
            1: showGoogleAds,
            2: !!showImis && !isImisConflicting,
            3: !metaIntegrationStatus.isDisabled && !!hasFacebook,
            4: !!showBlackbaudRaisersEdge && !isBlackbaudConflicting,
          },
        },
      )}
    </Nav>
  );

  return (
    <Nav indented={true} label={'Settings navigation'} name={'nav_settings_menu'} type={'vertical'}>
      <NavMenu
        data-command={'true'}
        data-command-description={t('User settings and preferences')}
        data-command-keywords={'settings,user,profile,notifications'}
        data-command-label={t('User Settings')}
        menu={userNav}
        name={'settings_user'}
        to={localUrl('/settings/user')}
      >
        {t('User')}
      </NavMenu>
      <NavMenu
        data-command={'true'}
        data-command-description={t('Account configuration and management')}
        data-command-keywords={'settings,account,configuration,management'}
        data-command-label={t('Account Settings')}
        menu={accountNav}
        name={'settings_account'}
        to={localUrl('/settings/account')}
      >
        {t('Account')}
      </NavMenu>

      {hasIntegrations && (
        <TrackBlocked
          featureName={'integrations'}
          locked={!integrationsMenuEnabled}
          requiredPackage={directIntegrations > 0 ? undefined : EPackageTiers.Essential}
        >
          <NavMenu
            data-command={'true'}
            data-command-description={t('Manage third-party integrations')}
            data-command-keywords={'settings,integrations,third-party'}
            data-command-label={t('Integrations')}
            menu={integrationsNav}
            name={'settings_integrations'}
            to={localUrl('/settings/integrations')}
            tooltip={integrationsTooltip}
          >
            {t('Integrations')}
          </NavMenu>
        </TrackBlocked>
      )}
      {hasBilling && (
        <NavMenu
          data-command={'true'}
          data-command-description={t('Manage billing and license settings')}
          data-command-keywords={'settings,billing,license,invoices'}
          data-command-label={t('Billing')}
          menu={billingNav}
          name={'settings_billing'}
          to={localUrl('/settings/billing')}
        >
          {t('Billing')}
        </NavMenu>
      )}
      {createNavItems(
        [
          [
            '/settings/pro-resources',
            'Access professional resources',
            'pro,resources,professional',
            'Pro Resources',
            t('Resources'),
          ],
        ],
        {
          localUrl,
          t,
          conditions: {
            0: proResourcesPageVisible,
          },
        },
      )}
    </Nav>
  );
}

export default observer(SettingsNav);
